import { useCanAccessGuardians } from "@/hooks/useCanAccessGuardians";
import { EventRow } from "@/components/ops/triggers/plannr-triggers/EventRow";
import { MissionHeader } from "@/components/missions/detail/MissionHeader";
import { TaskThread } from "@/components/missions/task-thread/TaskThread";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import type { EntityRef } from "../shared/entityDeepLink";
import { useEntityPreview } from "./entityPreviewData";
import { InteractionView } from "./InteractionView";
import { ToolCallView } from "./ToolCallView";

const NO_PENDING: ReadonlySet<string> = new Set();
const noop = async () => {};

export interface EntityPreviewProps {
  entity: EntityRef;
}

/**
 * Renders the preview for an entity using the *same* component its own app
 * uses, from data warmed by `prefetchEntityPreview`. Interaction and tool-call
 * previews are gated behind Guardians staff access.
 */
export function EntityPreview({ entity }: EntityPreviewProps) {
  const canAccessGuardians = useCanAccessGuardians();
  const { status, data } = useEntityPreview(entity, true);

  if (status === "loading" || status === "idle") {
    return <p className="text-xs text-slate-400">Loading preview…</p>;
  }
  if (status === "error" || !data) {
    return <p className="text-xs text-slate-400">Preview unavailable.</p>;
  }

  switch (data.kind) {
    case "trigger":
      return (
        <EventRow
          event={data.event}
          defaultOpen
          showApprovals={false}
          activePendingIds={NO_PENDING}
          onApprove={noop}
          onReject={noop}
        />
      );
    case "mission":
      return <MissionHeader mission={data.mission} agents={data.agents} />;
    case "task":
      return (
        <div className="h-[26rem] w-full overflow-hidden">
          <TaskThread taskId={data.taskId} variant="panel" readOnly />
        </div>
      );
    case "artifact":
      return <ArtifactCard artifact={data.artifact} variant="compact" />;
    case "interaction":
      if (!canAccessGuardians) return <StaffGate />;
      return <InteractionView interaction={data.interaction} />;
    case "tool_call": {
      if (!canAccessGuardians) return <StaffGate />;
      const step =
        data.stepIndex != null ? data.interaction.steps?.[data.stepIndex] : undefined;
      if (!step) {
        return <InteractionView interaction={data.interaction} highlightStep={data.stepIndex} />;
      }
      return <ToolCallView step={step} index={data.stepIndex} />;
    }
    default:
      return null;
  }
}

function StaffGate() {
  return (
    <p className="text-xs text-slate-400">
      This preview is available to Guardians staff only.
    </p>
  );
}
